import { useEffect } from 'react';

type UseAssistantLayoutWidthsOptions = {
  onOpenWidthChange?: (width: number) => void;
  onReservedWidthChange?: (width: number) => void;
  open: boolean;
  panelWidth: number;
  pinned: boolean;
  /** Must match packing (`view === 'chat' && …`), not raw `activeSources`. */
  sourcesOpen: boolean;
  sourcesWidth: number;
};

/**
 * Notifies the host layout how much horizontal space the assistant occupies.
 *
 * This hook does not own or resize panels; it derives totals from
 * {@link panelWidth}, {@link sourcesWidth}, {@link open}, {@link pinned}, and
 * whether the sources panel is open, then pushes them to optional callbacks:
 *
 * - {@link onReservedWidthChange} — width the page should reserve while the
 *   assistant is pinned and open (`panelWidth`). Zero when unpinned or closed.
 *   Consumers typically apply this as padding or a CSS variable on main content.
 * - {@link onOpenWidthChange} — total right-edge footprint while open
 *   (`panelWidth` plus {@link sourcesWidth} when sources are open). Zero when
 *   closed. Used to offset sibling overlays such as the assistant document
 *   preview and the page sidebar.
 *
 * Both callbacks reset to `0` on unmount so hosts do not retain stale offsets.
 */
export function useAssistantLayoutWidths({
  onOpenWidthChange,
  onReservedWidthChange,
  open,
  panelWidth,
  pinned,
  sourcesOpen,
  sourcesWidth,
}: UseAssistantLayoutWidthsOptions) {
  useEffect(() => {
    onReservedWidthChange?.(open && pinned ? panelWidth : 0);
  }, [open, pinned, panelWidth, onReservedWidthChange]);

  useEffect(() => {
    const reserved = sourcesOpen ? sourcesWidth : 0;
    onOpenWidthChange?.(open ? panelWidth + reserved : 0);
  }, [open, panelWidth, sourcesOpen, sourcesWidth, onOpenWidthChange]);

  useEffect(() => {
    return () => {
      onReservedWidthChange?.(0);
      onOpenWidthChange?.(0);
    };
  }, [onReservedWidthChange, onOpenWidthChange]);
}
